<script setup lang="ts">

import TheBottomSheet from '@/components/TheBottomSheet.vue'
import { ECOMMERCE_ITEM_PRICE_TYPE, type PlayerUpgrade } from '@/types/shared/player-config'
import { computed, ref } from 'vue'
import type { PlayerStats } from '@/types/shared/player'
import CoinIcon from '@/components/icons/CoinIcon.vue'
import { formatNumberFullTruncate } from '@/helpers/utils'
import BaseButton from '@/components/base/BaseButton.vue'
import { useUpgradeCooldown } from '@/composables/game/upgrade'
import ClockIcon from '@/components/icons/ClockIcon.vue'
import { T, useTranslate } from '@tolgee/vue'
import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue'
import webapp from '@/services/webapp'
import { useAuth } from '@/composables/auth'
import ThePremium from '@/components/ThePremium.vue'
import UpgradeCardIcon from '@/components/UpgradeCardIcon.vue'
import { useConfetti } from '@/composables/game/confetti'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import NumberFormatTooltip from '@/components/NumberFormatTooltip.vue'
import { hideTonPayment } from '@/composables/init'

const props = defineProps<{
	show: boolean
	isUpgrading: boolean
	player: PlayerStats
	upgrade: null | PlayerUpgrade
	canUpgradeForStars: boolean
	elapsedTime: number
	animationCooldown: number
	animationOffset: number
	hasCooldown?: boolean
	isHighlight?: boolean
}>()
const emit = defineEmits<{
	(e: 'hide'): void
	(e: 'upgrade', purchase: boolean, priceType?: ECOMMERCE_ITEM_PRICE_TYPE): void
	(e: 'onNewUpgrade', upgrade: PlayerUpgrade | null): void
}>()

const insufficientFunds = computed(() => {
  if (!props.upgrade) return true
  return props.player.coins < props.upgrade.price
})
const { t } = useTranslate()

const { profile } = useAuth()
const { showConfetti } = useConfetti()
const {
  formattedTime,
  canFastForward,
  doFastForward,
  fastForwardStarPrice,
  fastForwardTonPrice,
  formattedEstimatedPurchaseTime,
  isPurchasing,
} = useUpgradeCooldown(computed(() => props.upgrade))

const isLoading = computed(() => isPurchasing.value)
const selectedPriceType = ref<ECOMMERCE_ITEM_PRICE_TYPE | null>(null)
const isStarPurchaseLoading = computed(() => isLoading.value && selectedPriceType.value === ECOMMERCE_ITEM_PRICE_TYPE.STAR)
const isTonPurchaseLoading = computed(() => isLoading.value && selectedPriceType.value === ECOMMERCE_ITEM_PRICE_TYPE.TON)
const upgradeStarPrice = computed(() => props.upgrade?.ecommerce.fullUpgrade.prices.find(p => p.type === ECOMMERCE_ITEM_PRICE_TYPE.STAR))
const upgradeTonPrice = computed(() => props.upgrade?.ecommerce.fullUpgrade.prices.find(p => p.type === ECOMMERCE_ITEM_PRICE_TYPE.TON))

const hasReachedMaxLevel = computed(() => props.upgrade?.price === 0)
const showUpgradeForStars = computed(() => props.canUpgradeForStars && insufficientFunds.value && !props.hasCooldown && !hasReachedMaxLevel.value)
const isButtonDisabled = computed(() => hasReachedMaxLevel.value || (props.hasCooldown && !canFastForward.value) || (!props.hasCooldown && insufficientFunds.value && !showUpgradeForStars.value))
const buttonLabel = computed(() => {
  if (!props.upgrade) return ''
  if (hasReachedMaxLevel.value) {
    return t.value('upgrades.max_level_reached', 'Max level reached')
  }
  if (props.hasCooldown) {
    return formattedTime.value
  }
  if (insufficientFunds.value) {
    return t.value('buy_card.insufficient_funds', 'Insufficient funds')
  }
  return formatNumberFullTruncate(props.upgrade?.price ?? 0)
})

const cardStyle = computed(() => {
  if (props.upgrade?.highlight && props.upgrade.highlight?.backgroundImageUrl) {
    return {
      background: `linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, #000 100%), url(${props.upgrade.highlight.backgroundImageUrl}) no-repeat`,
      'background-size': 'cover',
    }
  }
  return undefined
})

async function onPrimaryClick() {
  emit('upgrade', insufficientFunds.value)
}

async function doPurchase(priceType: ECOMMERCE_ITEM_PRICE_TYPE) {
  if (!props.upgrade || isLoading.value) return
  if (canFastForward.value) {
    selectedPriceType.value = priceType
    try {
      const updatedUpgrade = await doFastForward(props.upgrade.id, priceType)
      if (updatedUpgrade) {
        await showConfetti()
        emit('onNewUpgrade', updatedUpgrade)
      }
    } finally {
      selectedPriceType.value = null
    }
  } else {
    emit('upgrade', insufficientFunds.value, priceType)
  }
}
</script>

<template>
  <TheBottomSheet
    :show="show"
    content-class="max-[375px]:pb-6"
    :prevent-close="isUpgrading || isLoading"
    @hide=" $emit('hide')"
  >
    <div
      v-if="upgrade"
      class="w-full flex flex-col items-center relative"
    >
      <StarsBalancePill
        v-if="canFastForward || showUpgradeForStars"
        class="absolute left-0"
      />
      <div
        v-if="!upgrade.highlight"
        class="flex items-center justify-center w-[120px] h-[120px]"
      >
        <UpgradeCardIcon
          :upgrade="upgrade"
          :elapsed-time="elapsedTime"
          :animation-cooldown="animationCooldown"
          :animation-offset="animationOffset"
          :has-cooldown="hasCooldown"
          :is-highlight="isHighlight"
          is-large
        />
      </div>
      <div
        v-else
        class="w-auto h-[120px] max-h-[120px] flex items-center justify-center rounded-lg"
        :style="cardStyle"
      >
        <img
          :src="upgrade.highlight.logoUrl"
          class="w-auto h-14 max-h-14 object-cover"
        >
      </div>
      <div class="mt-6 text-center text-2xl font-bold -tracking-[0.48px] text-light-white">
        {{ upgrade.name }}
      </div>
      <div
        v-if="upgrade.description"
        class="text-center text-base font-medium text-white/60 max-w-[379px]"
      >
        {{ upgrade.description }}
      </div>
      <div v-if="upgrade.highlight?.cta && upgrade.highlight.ctaUrl">
        <BaseButton
          style-type="tertiary"
          button-class="h-11"
          class="mt-6 pl-4 pr-2"
          @click="webapp.openLink(upgrade.highlight.ctaUrl.replace(/PLAYER_RC/g, profile.referralCode))"
        >
          {{ upgrade.highlight?.cta }}
          <template #icon>
            <ArrowRightIcon />
          </template>
        </BaseButton>
      </div>
      <div
        v-if="!hasReachedMaxLevel"
        class="max-[375px]:mt-4 mt-8 flex flex-col gap-y-1.5 items-center"
      >
        <div
          class="text-lg font-semibold text-white flex items-center gap-x-1.5 px-8 py-3 rounded-lg bg-dark-bottom-layer"
        >
          <NumberFormatTooltip
            :number="upgrade.profit"
            :formatting-type="'full'"
          >
            <span>
              {{ formatNumberFullTruncate(upgrade.profit) }}
            </span>
          </NumberFormatTooltip>
          <ArrowRightIcon class="size-6 min-w-[24px]" />
          <NumberFormatTooltip
            :number="upgrade.profitDelta + upgrade.profit"
            :formatting-type="'full'"
          >
            <span>
              {{ formatNumberFullTruncate(upgrade.profitDelta + upgrade.profit) }}
            </span>
          </NumberFormatTooltip>
        </div>
        <div class="flex items-center gap-x-2">
          <div class="text-sm leading-6 text-white/60 font-medium">
            <T
              key-name="generic.profit_per_hour"
              default-value="Trading per hour"
            />
          </div>
          <div
            class="flex items-center gap-x-1"
            :class="insufficientFunds && 'opacity-60'"
          >
            <CoinIcon
              class="w-[10px] h-4 min-w-[10px] text-white"
            />
            <NumberFormatTooltip
              :number="upgrade.profitDelta"
              :formatting-type="'full'"
            >
              <div
                class="text-sm font-semibold -tracking-[0.28px] text-white"
              >
                +{{ formatNumberFullTruncate(upgrade.profitDelta) }}
              </div>
            </NumberFormatTooltip>
          </div>
        </div>
      </div>
      <div class="max-[375px]:mt-4 mt-8 flex flex-col w-full">
        <div
          v-if="canFastForward || showUpgradeForStars"
          class="bg-dark-top-layer border border-dark-top-most-layer rounded-lg p-2 pb-3 mb-4 flex flex-col text-base font-medium text-white/60 items-center justify-center text-center "
        >
          <template v-if="canFastForward">
            <p>
              <T
                key-name="upgrades.speed_up.description1"
                default-value="You need to wait to upgrade this card."
              />
            </p>
            <p>
              <T
                key-name="upgrades.speed_up.description2.wait"
                default-value="Wait"
              />
              <span class="text-white/[87%] font-semibold"> {{ ` ${formattedTime} ` }}</span>
              <T
                key-name="upgrades.speed_up.description2.or_speed_up_now"
                default-value="or speed up now."
              />
            </p>
          </template>
          <template v-else>
            <p>
              <T
                key-name="upgrades.insufficient_funds.description1"
                default-value="You don’t have enough Dropee coins."
              />
            </p>
            <p>
              <T
                key-name="upgrades.insufficient_funds.description2.wait"
                default-value="Wait"
              />
              <span class="text-white/[87%] font-semibold"> {{ ` ${formattedEstimatedPurchaseTime} ` }}</span>
              <T
                key-name="upgrades.insufficient_funds.description2.or_purchase_now"
                default-value="or purchase now."
              />
            </p>
          </template>
        </div>
        <div
          v-if="showUpgradeForStars || canFastForward"
          class="w-full flex flex-col"
        >
          <BaseButton
            v-if="fastForwardStarPrice || upgradeStarPrice"
            class="w-full"
            :loading="isStarPurchaseLoading"
            spinner-class="w-[18px] h-[18px] min-w-[18px]"
            @click="doPurchase(ECOMMERCE_ITEM_PRICE_TYPE.STAR)"
          >
            <span
              v-if="!isStarPurchaseLoading"
              class="flex items-center gap-x-2"
            >
              <T
                v-if="canFastForward"
                key-name="upgrades.speed_up_for"
                default-value="Speed up for"
              />
              <T
                v-else
                key-name="upgrades.buy_with"
                default-value="Buy with"
              />
              <ThePremium :with-tooltip="false" />
              <span class="-ml-0.5">{{
                canFastForward ? fastForwardStarPrice?.amount : upgradeStarPrice?.amount
              }}</span>
            </span>
            <T
              v-else
              key-name="lingo.giveaway.transaction.loading_cta"
              default-value="Processing..."
            />
          </BaseButton>
          <BaseButton
            v-if="!hideTonPayment && (fastForwardTonPrice || upgradeTonPrice)"
            style-type="tertiary"
            :loading="isTonPurchaseLoading"
            class="mt-3 w-full"
            spinner-class="w-[18px] h-[18px] min-w-[18px]"
            @click="doPurchase(ECOMMERCE_ITEM_PRICE_TYPE.TON)"
          >
            <span
              v-if="!isTonPurchaseLoading"
              class="flex items-center gap-x-2"
            >
              <T
                v-if="canFastForward"
                key-name="upgrades.speed_up_for"
                default-value="Speed up for"
              />
              <T
                v-else
                key-name="upgrades.buy_with"
                default-value="Buy with"
              />
              <img
                src="@/assets/images/ton-symbol.png"
                class="size-5 min-w-[20px] object-cover"
              >
              <span>{{ canFastForward ? fastForwardTonPrice?.amount : upgradeTonPrice?.amount }}</span>
            </span>
            <T
              v-else
              key-name="lingo.giveaway.transaction.loading_cta"
              default-value="Processing..."
            />
          </BaseButton>
        </div>
        <BaseButton
          v-else
          class="w-full"
          button-class="px-4"
          :style-type="isButtonDisabled ? 'primary' : 'secondary'"
          :disabled="isButtonDisabled"
          :loading="isUpgrading || isLoading"
          @click="onPrimaryClick"
        >
          <template
            #leftIcon
            v-if="hasCooldown && !canFastForward"
          >
            <ClockIcon
              class="-mr-2 h-full w-full min-w-[24px]"
            />
          </template>
          <div
            class="flex items-center gap-x-2"
            :class="hasCooldown && 'min-w-[81px] justify-center block'"
          >
            <template v-if="!isLoading && !hasReachedMaxLevel">
              <CoinIcon
                v-if="!hasCooldown && !insufficientFunds"
                class="w-3 h-5 min-w-[12px]"
              />
            </template>
            <span>{{ buttonLabel }}</span>
          </div>
        </BaseButton>
      </div>
    </div>
  </TheBottomSheet>
</template>

<style scoped>

</style>
